import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';
import { clsx } from 'clsx';
import { type ReactNode, useRef, useState } from 'react';

import { SgInput } from '@/components/sg-design-system/components/input/SgInput';
import type { OptionSide } from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';

type BidAskInputProps = {
  value: BidAsk<number | null | undefined>;
  side: OptionSide | undefined;
  color?: boolean;
  'data-e2e'?: string;
  format?: (value: number | null | undefined) => string;
  suffix?: ReactNode;
  readOnly?: boolean;
} & (
  | {
      editMode: 'bidAsk';
      onChange: (value: BidAsk<number | null>) => void;
    }
  | {
      editMode: 'single';
      onChange: (value: number | null) => void;
    }
);

function defaultFormat(value: number | null | undefined): string {
  return isDefined(value) ? `${value}` : '';
}

function isValidNumber(num: number | null | undefined): num is number {
  return isDefined(num) && !Number.isNaN(num);
}

export function BidAskInput({
  value,
  editMode,
  onChange,
  side,
  color,
  'data-e2e': dataE2e,
  format = defaultFormat,
  suffix,
  readOnly = false,
}: BidAskInputProps) {
  const [mode, setMode] = useState<'edit' | 'display'>('display');
  const inputRef = useRef<HTMLInputElement>(null);

  const { bid, ask } = value;
  const formattedBid = format(bid);
  const formattedAsk = format(ask);
  const formattedValue = `${formattedBid} / ${formattedAsk}`;
  const editedValue = useRef<string | undefined>(undefined);

  function edit() {
    if (readOnly) {
      return;
    }
    setMode('edit');
    editedValue.current = editMode === 'bidAsk' ? formattedValue : side === 'Buy' ? formattedAsk : formattedBid;
    setTimeout(() => {
      inputRef.current?.select();
    });
  }

  function display() {
    setMode('display');
  }

  function handleChange(value: string) {
    if (editMode === 'bidAsk') {
      const bidAsk = parseBidAsk(value);
      if (isDefined(bidAsk)) {
        onChange(bidAsk);
      }
    } else {
      const val = parseSingle(value);
      if (isDefined(val)) {
        onChange(val);
      }
    }
    display();
  }

  function parseBidAsk(value: string): BidAsk<number | null> | undefined {
    if (isEmpty(value.trim())) {
      return { bid: null, ask: null };
    }
    const [bid, ask] = value.split('/').map((value) => Number.parseFloat(value.trim()));
    if (isValidNumber(bid)) {
      return { bid, ask: isDefined(ask) && !Number.isNaN(ask) ? ask : bid };
    }
    return undefined;
  }

  function parseSingle(value: string): number | null | undefined {
    if (isEmpty(value.trim())) {
      return null;
    }
    const num = Number.parseFloat(value.trim());
    if (isValidNumber(num)) {
      return num;
    }
    return undefined;
  }

  if (mode === 'edit') {
    return (
      <SgInput
        data-e2e={`${dataE2e}-input`}
        ref={inputRef}
        onChange={handleChange}
        onBlur={() => setMode('display')}
        value={editedValue.current}
        size="sm"
      />
    );
  }

  if (isNotDefined(bid) || isNotDefined(ask)) {
    return (
      <div
        data-e2e={`${dataE2e}-text`}
        onClick={edit}
        className="form-control form-control-sm bg-primary-alt d-flex flex-between"
      />
    );
  }

  return (
    <div
      data-e2e={`${dataE2e}-text`}
      onClick={edit}
      className="form-control form-control-sm bg-primary-alt d-flex flex-between"
    >
      <div>
        <span
          className={clsx(
            'fw-bold flex-fill text-center monospace-numbers',
            color
              ? {
                  'text-danger': side === 'Sell' && bid < 0,
                  'text-success': side === 'Sell' && bid >= 0,
                }
              : undefined,
            { 'text-secondary': side !== 'Sell' },
          )}
          data-e2e={dataE2e}
        >
          {formattedBid}
        </span>
        <span> / </span>
        <span
          className={clsx(
            'fw-bold flex-fill text-center monospace-numbers',
            color
              ? {
                  'text-danger': side === 'Buy' && ask < 0,
                  'text-success': side === 'Buy' && ask >= 0,
                }
              : undefined,
            { 'text-secondary': side !== 'Buy' },
          )}
          data-e2e={dataE2e}
        >
          {formattedAsk}
        </span>
      </div>
      {suffix}
    </div>
  );
}
